import React from "react";
import { FormField, FormItem, FormLabel, FormControl, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { UseFormReturn } from "react-hook-form";
import { PatchPolicyFormValues } from "../schema";

interface SeverityRemediationSectionProps {
  form: UseFormReturn<PatchPolicyFormValues>;
  severity: 'critical' | 'high' | 'medium' | 'low';
  cvssRange: string;
  barCount: number;
  colorClass: string;
}

const actionOptions = [
  { value: 'enforce_immediate', label: 'Enforce update upon detection' },
  { value: 'enforce_timeframe', label: 'Enforce update on a timeframe' },
  { value: 'notify_only', label: 'Notify only (manual remediation)' },
  { value: 'no_action', label: 'No action' },
];

const timezoneOptions = [
  { value: 'device_local', label: "Device's local timezone" },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT)' },
];

export function SeverityRemediationSection({
  form,
  severity,
  cvssRange,
  barCount,
  colorClass,
}: SeverityRemediationSectionProps) {
  const selectedAction = form.watch(`${severity}.action`);
  const showTimeframeFields = selectedAction === 'enforce_timeframe';

  const severityLabel = severity.charAt(0).toUpperCase() + severity.slice(1);
  
  return (
    <div className="border rounded-lg p-4 space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className={`w-2 h-8 rounded-sm ${
                i < barCount ? colorClass : 'bg-muted'
              }`}
            />
          ))}
        </div>
        <div>
          <h3 className="font-semibold">{severityLabel}</h3>
          <p className="text-sm text-muted-foreground">CVSS {cvssRange}</p>
        </div>
      </div>

      <FormField
        control={form.control}
        name={`${severity}.action`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Action</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select action" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {actionOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormItem>
        )}
      />

      {showTimeframeFields && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name={`${severity}.timeframeValue`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Timeframe Value</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      placeholder="1"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={`${severity}.timeframeUnit`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unit</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="hours">Hours</SelectItem>
                      <SelectItem value="days">Days</SelectItem>
                      <SelectItem value="weeks">Weeks</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name={`${severity}.timezone`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Timezone</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {timezoneOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>
                  Choose when updates should be applied based on timezone
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={`${severity}.timeOfDay`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Time of Day</FormLabel>
                <FormControl>
                  <Input
                    type="time"
                    placeholder="12:00"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Specific time to apply updates (e.g., 12:00 PM)
                </FormDescription>
              </FormItem>
            )}
          />
        </>
      )}
    </div>
  );
}
